import { FolderOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { Button, Empty } from "antd";
import Link from "next/link";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { ActivitiesProductTour } from "../../../components/agro/activities/ProductTour";
import { LocalityFormSelect } from "../../../components/agro/localities/LocalityFormSelect";
import { ChangeLogSidebar } from "../../../components/shared/ChangeLog/Sidebar";
import { HelpTooltip } from "../../../components/shared/HelpTooltip";
import { useItemSidebarContext } from "../../../components/shared/ItemSidebar/Provider";
import { ListView } from "../../../components/shared/ListView";
import { DestroyItem } from "../../../components/shared/ListView/DestroyItem";
import { DiscardItem } from "../../../components/shared/ListView/DiscardItem";
import { ItemDetails } from "../../../components/shared/ListView/ItemDetails";
import { ListMenu } from "../../../components/shared/ListView/ListMenu";
import { Space } from "../../../components/shared/Space";
import BasicLayout from "../../../layouts/BasicLayout";
import { BLANK_SYMBOL } from "../../../lib/constants";
import { formatDetailsLink, formatYesNo } from "../../../lib/formats/common";
import {
  DestroyableType,
  DiscardableType,
  ImportModulesType,
} from "../../../lib/graphql/generated";
import {
  useActivities,
  useActivityDestroy,
  useActivityDiscard,
  useActivityLaborForceTypesFormat,
  useActivityProgressMeasureTypesFormat,
} from "../../../lib/hooks/agro/activities";
import { useActivityCategoryOptions } from "../../../lib/hooks/agro/activityCategories";
import {
  useWorkOrderTypeFormat,
  useWorkOrderTypeOptions,
} from "../../../lib/hooks/agro/workOrders";
import { useCurrentUser, usePermissions } from "../../../lib/hooks/basic/users";
import routes, { routerPush } from "../../../lib/routes";

const messages = defineMessages({
  activities: { id: "activities", defaultMessage: "activities" },
  activity: { id: "activities.entityName", defaultMessage: "activity" },
  description: { id: "activities.description", defaultMessage: "description" },
  workOrderType: {
    id: "activities.workOrderType",
    defaultMessage: "workOrderType",
  },
  cropStages: { id: "cropStages", defaultMessage: "cropStages" },
  category: { id: "activities.category", defaultMessage: "category" },
  internalId: { id: "internalId", defaultMessage: "internalId" },
  workdayHours: {
    id: "activities.workdayHours",
    defaultMessage: "workdayHours",
  },
  progressUnit: {
    id: "activities.progressUnit",
    defaultMessage: "progressUnit",
  },
  progressMeasureType: {
    id: "activities.progressMeasureType",
    defaultMessage: "progressMeasureType",
  },
  laborForceType: {
    id: "activities.laborForceType",
    defaultMessage: "laborForceType",
  },
  pageTitle: { id: "activities.pageTitle", defaultMessage: "Activities" },
  photo: { id: "photo", defaultMessage: "photo" },
  created: { id: "created", defaultMessage: "created" },
});

function Activities() {
  const intl = useIntl();
  const entityName = intl.formatMessage(messages.activity);
  const permissions = usePermissions((p) => p.activity);
  const activityCategoryPermissions = usePermissions((p) => p.activityCategory);
  const { currentLocalityIds, currentTenant } = useCurrentUser();
  const { formatWorkOrderType } = useWorkOrderTypeFormat();
  const formatActivityProgressMeasureType =
    useActivityProgressMeasureTypesFormat();
  const formatActivityLaborForceType = useActivityLaborForceTypesFormat();

  const disableActions = !currentLocalityIds?.length;

  return (
    <BasicLayout
      title={intl.formatMessage(messages.pageTitle)}
      breadcrumbs={[
        {
          path: routes.agro.activities.index,
          breadcrumbName: intl.formatMessage(messages.pageTitle),
        },
      ]}
      tooltip={intl.formatMessage(messages.description)}
      headerContent={
        <ListMenu
          allowNew={permissions?.write}
          newRoute={routes.agro.activities.new}
          importProps={{
            moduleName: ImportModulesType.Activity,
            btnText: intl.formatMessage(messages.pageTitle),
            customRoute: routes.imports.wizard(ImportModulesType.Activity),
          }}
          exportType="Activities"
          left={
            <Space>
              {activityCategoryPermissions?.read && (
                <Link
                  href={routes.agro.activityCategories.index}
                  legacyBehavior
                >
                  <Button
                    icon={<FolderOutlined />}
                    href={routes.agro.activityCategories.index}
                    data-action="activity-categories"
                  >
                    <span>
                      <FormattedMessage id="activityCategories" />
                    </span>
                  </Button>
                </Link>
              )}

              <HelpTooltip
                disabled={!disableActions}
                placement="right"
                title={
                  <FormattedMessage
                    id="activities.noLocality"
                    defaultMessage="Please {link} to create activities"
                    values={{
                      link: (
                        <Link
                          href={routes.localities.index}
                          style={{ color: "#fff" }}
                        >
                          <u>
                            <FormattedMessage
                              id="localities.addAtLeastOne"
                              defaultMessage="add at least one locality"
                            />
                          </u>
                        </Link>
                      ),
                    }}
                  />
                }
                trigger="hover"
              >
                <Link
                  href={disableActions ? "" : routes.agro.activities.catalog}
                  legacyBehavior
                >
                  <Button
                    type="default"
                    icon={<UnorderedListOutlined />}
                    href={routes.agro.activities.catalog}
                    disabled={disableActions}
                    data-action="activity-catalog"
                  >
                    <span className="hide-xs">
                      <FormattedMessage
                        id="activities.chooseFromCatalog"
                        defaultMessage="Activity Catalog"
                      />
                    </span>
                  </Button>
                </Link>
              </HelpTooltip>
            </Space>
          }
        />
      }
      authorize={(p) => !!p?.activity?.read}
      fullContentHeight={false}
      videoId="lxn-UytvGjU"
    >
      <ListView
        query={useActivities}
        queryOptions={{
          variables: { filter: { localityId: currentLocalityIds } },
        }}
        tableProps={{
          fixedHeader: true,
          locale: {
            emptyText: <EmptyText entityName={entityName} />,
          },
        }}
        columns={[
          {
            title: intl.formatMessage(messages.internalId),
            dataIndex: "internalId",
            width: "8rem",
            sorter: true,
          },
          {
            dataIndex: "name",
            sorter: true,
            render: (title, { id }) =>
              formatDetailsLink({
                id,
                route: routes.agro.activities.details,
                title,
                target: "",
              }),
          },
          {
            title: intl.formatMessage(messages.category),
            dataIndex: "categoryName",
            render: (_, a) => a.category.name,
            sorter: true,
          },
          currentTenant.features.workOrderTypes && {
            title: intl.formatMessage(messages.workOrderType),
            dataIndex: "workOrderType",
            render: formatWorkOrderType,
            sorter: true,
          },
          currentTenant.features.advancedActivities && {
            title: intl.formatMessage(messages.workdayHours),
            dataIndex: "workdayHours",
            sorter: true,
            width: "10rem",
          },
          currentTenant.features.advancedActivities && {
            title: intl.formatMessage(messages.progressUnit),
            width: "14rem",
            dataIndex: "progressUnitName",
            render: (_, a) => a.progressUnit.name,
            sorter: true,
          },
          {
            title: <FormattedMessage id="localities.pageTitle" />,
            dataIndex: "localities",
            render: (_, a) =>
              a.localities.length > 0 ? (
                a.localities.map((l) => l.name).join(", ")
              ) : (
                <FormattedMessage id="localities.all" />
              ),
          },
        ]}
        filters={[
          {
            name: "name",
            type: "string",
          },
          {
            title: intl.formatMessage(messages.category),
            name: "categoryId",
            type: "select",
            optionsHook: useActivityCategoryOptions,
          },
          {
            title: intl.formatMessage(messages.workOrderType),
            name: "workOrderType",
            type: "select",
            showGroups: true,
            hidden: !currentTenant.features.workOrderTypes,
            optionsHook: useWorkOrderTypeOptions,
          },
          {
            title: intl.formatMessage(messages.internalId),
            name: "internalId",
            type: "string",
          },
          {
            name: "localityId",
            type: "custom",
            initialValue: currentLocalityIds,
            render: () => <LocalityFormSelect />,
          },
          {
            title: intl.formatMessage(messages.created),
            name: "createdAt",
            type: "date",
          },
          {
            title: (
              <FormattedMessage
                id="showDiscarded"
                defaultMessage="showDiscarded"
              />
            ),
            name: "showAll",
            type: "switch",
          },
        ]}
        disabledRecord={(m) => !!m.discardedAt}
        entityName={entityName}
        pluralEntityName={intl.formatMessage(messages.activities)}
        permissions={permissions}
        labelKey="name"
        bulkOptions={{
          destroyableType: DestroyableType.Activity,
          discardableType: DiscardableType.Activity,
          resetQueryCache: ["activities", "onboardingStatus"],
        }}
        sidebarActions={{
          details: ({ item, closeSidebar }) => (
            <ItemDetails
              entityName={entityName}
              entityUrl={routes.agro.activities.index}
              item={item}
              onEdit={() => routerPush(item.id, routes.agro.activities.edit)}
              onClose={closeSidebar}
              permissions={permissions}
              destroy
              changeLog
              fields={[
                {
                  name: "name",
                  render: (a) => a.name,
                },
                {
                  label: intl.formatMessage(messages.internalId),
                  render: (a) => a.internalId || BLANK_SYMBOL,
                },
                {
                  label: <FormattedMessage id="description" />,
                  render: (a) => a.description || BLANK_SYMBOL,
                },
                {
                  label: intl.formatMessage(messages.category),
                  render: (a) => a.category.name,
                },
                currentTenant.features.workOrderTypes && {
                  label: intl.formatMessage(messages.workOrderType),
                  render: (a) => formatWorkOrderType(a.workOrderType),
                },
                currentTenant.features.advancedActivities && {
                  label: intl.formatMessage(messages.workdayHours),
                  render: (a) => a.workdayHours,
                },
                currentTenant.features.advancedActivities && {
                  label: <FormattedMessage id="activities.trackCycles" />,
                  render: (a) => formatYesNo(a.trackCycles),
                },
                currentTenant.features.advancedActivities &&
                  currentTenant.features.workSchedules && {
                    label: <FormattedMessage id="activities.trackTime" />,
                    render: (a) => formatYesNo(a.trackTime),
                  },
                currentTenant.features.advancedActivities && {
                  label: <FormattedMessage id="activities.trackTools" />,
                  render: (a) => formatYesNo(a.trackTools),
                },
                currentTenant.features.advancedActivities && {
                  label: intl.formatMessage(messages.progressUnit),
                  render: (a) => a.progressUnit.name,
                },
                currentTenant.features.advancedActivities && {
                  label: intl.formatMessage(messages.progressMeasureType),
                  render: (a) =>
                    formatActivityProgressMeasureType(a.progressMeasureType),
                },
                currentTenant.features.advancedActivities && {
                  label: (
                    <FormattedMessage
                      id="activities.doneOver"
                      defaultMessage="Done over"
                    />
                  ),
                  render: (a) => (
                    <FormattedMessage
                      id={
                        a.useTotalArea
                          ? "cropFields.totalArea"
                          : "cropFields.effectiveArea"
                      }
                    />
                  ),
                },
                currentTenant.features.advancedActivities && {
                  label: intl.formatMessage(messages.laborForceType),
                  render: (a) => formatActivityLaborForceType(a.laborForceType),
                },
              ]}
            />
          ),
          discard: ({ item, setAction, closeSidebar }) => (
            <DiscardItem
              item={item}
              entityName={entityName}
              mutation={useActivityDiscard}
              mutationOptions={{ refetchQueries: ["Activities"] }}
              onMutate={(result) => result.activityDiscard}
              onCancel={() => setAction("details")}
              onSuccess={closeSidebar}
            />
          ),
          destroy: ({ item, setAction, closeSidebar }) => (
            <DestroyItem
              item={item}
              entityName={entityName}
              mutation={useActivityDestroy}
              mutationOptions={{
                refetchQueries: ["Activities", "OnboardingStatus"],
              }}
              onMutate={(result) => result.activityDestroy}
              onCancel={() => setAction("details")}
              onSuccess={closeSidebar}
            />
          ),
          changeLog: ({ item, setAction }) => (
            <ChangeLogSidebar
              id={item.id}
              type="Agro::Activity"
              onClose={() => setAction("details")}
            />
          ),
        }}
      />

      <ActivitiesProductTour />
    </BasicLayout>
  );
}

function EmptyText({ entityName }: { entityName: string }) {
  const { setCurrentAction } = useItemSidebarContext();

  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_DEFAULT}
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexFlow: "column",
      }}
      description={
        <FormattedMessage
          id="activities.noData"
          defaultMessage="No data"
          values={{
            new: (
              <Link href={routes.agro.activities.new}>
                <FormattedMessage id="new" /> {entityName}
              </Link>
            ),
            catalog: (
              <Link href={routes.agro.activities.catalog}>
                <FormattedMessage id="activities.chooseFromCatalog" />
              </Link>
            ),
            import: (
              <a onClick={() => setCurrentAction("import")}>
                <FormattedMessage
                  id="importingExcel"
                  defaultMessage="importing records via excel"
                />
              </a>
            ),
            br: <br />,
          }}
        />
      }
    />
  );
}

export default Activities;
